import { useEffect, useState } from "react";
import { Loader2, Sparkles, Upload, Wand2, CreditCard, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * MediaManager — universal image editor + AI-credit top-up widget.
 *
 * Renders one card per configured slot. Each card exposes:
 *   • Upload  — <input type=file> → POST /api/uploads/image → POST
 *               /api/business/{id}/media/set
 *   • AI Regen — POST /api/business/{id}/media/regenerate (35¢/credit)
 *
 * When the user's credit balance hits 0 the AI Regen button opens the
 * Stripe Checkout modal directly (`BuyCreditsButton`). On successful
 * payment (?ai_credits_session_id=… on return) the Dashboard reads the
 * new balance from GET /api/user/ai-credits.
 *
 * Slot config:
 *   - Restaurant biz: [hero, about_1, about_2]
 *   - Other biz     : [hero] (single slot; sections editor covers the rest)
 */

const SLOT_LABELS = {
    hero: "Hero (top of homepage)",
    about_1: "About — interior photo",
    about_2: "About — signature dish",
};

function ImageSlot({ slot, label, currentUrl, biz, credits, onCredits, onUpdated, onOpenBuy }) {
    const [busy, setBusy] = useState(null);
    const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

    const setSlot = async (imageUrl) => {
        const { data } = await api.post(`/business/${biz.business_id}/media/set`, { slot, image_url: imageUrl });
        onUpdated(slot, data.image_url);
    };

    const regen = async () => {
        if (credits === 0) { onOpenBuy(); return; }
        setBusy("regen");
        try {
            const { data } = await api.post(`/business/${biz.business_id}/media/regenerate`, { slot });
            onUpdated(slot, data.image_url);
            onCredits(data.balance);
            toast.success(`New AI image · ${data.balance} credits left`);
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (e?.response?.status === 402) {
                onCredits(0);
                onOpenBuy();
            } else {
                toast.error(typeof d === "string" ? d : "AI generation failed");
            }
        } finally {
            setBusy(null);
        }
    };

    const upload = async (file) => {
        if (!file) return;
        setBusy("upload");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const up = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await setSlot(up.data.image_url);
            toast.success("Image uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="border-2 border-black rounded-xl overflow-hidden bg-white" data-testid={`slot-${slot}`}>
            <div className="aspect-video bg-gray-100 relative">
                {currentUrl ? (
                    <img src={resolveUrl(currentUrl)} alt={label} className="w-full h-full object-cover" data-testid={`slot-${slot}-img`} />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">No image yet</div>
                )}
            </div>
            <div className="p-4">
                <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">{label}</div>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <label
                        data-testid={`slot-${slot}-upload-label`}
                        className={`flex-1 cursor-pointer px-3 py-2.5 border-2 border-black rounded-xl bg-white font-bold flex items-center justify-center gap-2 nb-press text-sm ${busy === "upload" ? "opacity-50" : ""}`}
                    >
                        {busy === "upload" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Upload
                        <input data-testid={`slot-${slot}-file-input`} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} disabled={!!busy} />
                    </label>
                    <button
                        data-testid={`slot-${slot}-regen-btn`}
                        onClick={regen}
                        disabled={!!busy}
                        className="flex-1 px-3 py-2.5 border-2 border-black rounded-xl bg-[#111] text-white font-bold flex items-center justify-center gap-2 nb-press text-sm disabled:opacity-50"
                    >
                        {busy === "regen" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                        AI Regen
                    </button>
                </div>
            </div>
        </div>
    );
}

function BuyCreditsModal({ open, onClose }) {
    const [packs, setPacks] = useState([]);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        if (open) {
            api.get("/user/ai-credits/packs").then(({ data }) => setPacks(data.packs)).catch(() => setPacks([]));
        }
    }, [open]);

    if (!open) return null;

    const buy = async (packId) => {
        setBusy(packId);
        try {
            const origin = window.location.origin;
            const { data } = await api.post("/user/ai-credits/checkout", { pack: packId, origin_url: origin });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout");
            setBusy(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="buy-credits-modal">
            <div className="bg-white rounded-2xl border-2 border-black max-w-md w-full p-6 relative">
                <button
                    data-testid="buy-credits-close"
                    onClick={onClose}
                    className="absolute right-4 top-4 p-1 text-gray-500 hover:text-black"
                    aria-label="Close"
                >
                    <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-[#8B5CF6]" />
                    <h3 className="text-xl font-extrabold">Buy AI image credits</h3>
                </div>
                <p className="text-sm text-gray-600 mb-5">
                    Every user gets 5 free credits. Additional packs are billed via secure Stripe Checkout.
                </p>
                <div className="space-y-3">
                    {packs.map((p) => (
                        <button
                            key={p.id}
                            data-testid={`credit-pack-${p.id}`}
                            onClick={() => buy(p.id)}
                            disabled={!!busy}
                            className="w-full flex items-center justify-between p-4 border-2 border-black rounded-xl bg-white hover:bg-gray-50 nb-press disabled:opacity-50"
                        >
                            <div className="text-left">
                                <div className="font-extrabold text-lg">{p.credits} AI images</div>
                                <div className="text-xs text-gray-500">{p.per_image_cents.toFixed(1)}¢ per image</div>
                            </div>
                            <div className="text-lg font-extrabold">
                                {busy === p.id ? <Loader2 className="w-5 h-5 animate-spin" /> : `$${p.price_usd.toFixed(2)}`}
                            </div>
                        </button>
                    ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-4">
                    Credits never expire. You&apos;ll be returned to your dashboard after payment.
                </p>
            </div>
        </div>
    );
}

export default function MediaManager({ biz }) {
    const [credits, setCredits] = useState(null);
    const [slotOverrides, setSlotOverrides] = useState({});
    const [buyOpen, setBuyOpen] = useState(false);

    const loadCredits = async () => {
        try {
            const { data } = await api.get("/user/ai-credits");
            setCredits(data.balance);
        } catch { /* non-fatal */ }
    };

    useEffect(() => { loadCredits(); }, []);

    // Auto-verify Stripe checkout return
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sid = params.get("ai_credits_session_id");
        if (sid) {
            api.get(`/billing/checkout/${sid}`).then(({ data }) => {
                if (data.payment_status === "paid") {
                    toast.success("Payment received — credits added");
                    loadCredits();
                }
                // Strip the session_id so a refresh doesn't re-verify.
                const url = new URL(window.location.href);
                url.searchParams.delete("ai_credits_session_id");
                window.history.replaceState({}, "", url.toString());
            }).catch(() => { /* silent */ });
        } else if (params.get("ai_credits") === "cancel") {
            toast.info("Purchase cancelled");
            const url = new URL(window.location.href);
            url.searchParams.delete("ai_credits");
            window.history.replaceState({}, "", url.toString());
        }
    }, []);

    if (!biz) return null;

    const isRestaurant = biz.industry_template === "restaurant";
    const slots = isRestaurant ? ["hero", "about_1", "about_2"] : ["hero"];

    const currentUrl = (slot) => {
        if (slotOverrides[slot]) return slotOverrides[slot];
        const wc = biz.website_content || {};
        if (slot === "hero") return wc.hero_image_url || "";
        if (slot === "about_1") return wc.about_image_1 || "";
        if (slot === "about_2") return wc.about_image_2 || "";
        return "";
    };

    return (
        <div data-testid="media-manager">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <h3 className="text-xl font-extrabold">Site images</h3>
                    <p className="text-sm text-gray-600 mt-1">
                        Upload your own photos or regenerate with AI. First 5 AI generations free, then 35¢ each.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {credits !== null && (
                        <span
                            data-testid="credits-badge"
                            className="text-[10px] uppercase tracking-widest font-extrabold px-2.5 py-1.5 rounded-full border-2 border-black bg-[#FFF7E6]"
                        >
                            <Sparkles className="w-3 h-3 inline mr-1" /> {credits} AI credits
                        </span>
                    )}
                    <button
                        data-testid="buy-credits-btn"
                        onClick={() => setBuyOpen(true)}
                        className="text-xs px-3 py-1.5 border-2 border-black rounded-full bg-white font-extrabold hover:bg-[#F5F0FF] nb-press flex items-center gap-1.5"
                    >
                        <CreditCard className="w-3.5 h-3.5" /> Buy more
                    </button>
                </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {slots.map((slot) => (
                    <ImageSlot
                        key={slot}
                        slot={slot}
                        label={SLOT_LABELS[slot] || slot}
                        currentUrl={currentUrl(slot)}
                        biz={biz}
                        credits={credits ?? 0}
                        onCredits={setCredits}
                        onUpdated={(s, url) => setSlotOverrides((o) => ({ ...o, [s]: url }))}
                        onOpenBuy={() => setBuyOpen(true)}
                    />
                ))}
            </div>
            <BuyCreditsModal open={buyOpen} onClose={() => setBuyOpen(false)} />
        </div>
    );
}
